import { mountStoreDevtool } from 'simple-zustand-devtools'
import { Email, Endereco, SocioType, Telefone } from 'types/registro'
import { create } from 'zustand'
import { combine, createJSONStorage, persist } from 'zustand/middleware'
import {
  DefaultDadosCliente,
  DevolucaoForm,
  FormsContatoData,
  FormsPF,
  FormsPJ
} from './registroStore'
import { ContaBancariaData, Informacoespix } from 'types/contas'

const principalKeyMap = {
  enderecos: 'endereco_principal',
  telefones: 'telefone_principal',
  emails: 'e_mail_principal',
  socios: 'sn_principal'
} as const
export type KeyObjectCliente = keyof typeof principalKeyMap

export type PrincipalKeyMap = typeof principalKeyMap

const updateForm = <T, K extends keyof T>(form: T, key: K, partialForm: Partial<T[K]>): T => ({
  ...form,
  [key]: {
    ...form[key],
    ...partialForm
  }
})

const addOrUpdatePrincipal = <T extends { sn_principal?: string }>(array: T[], item: T): T[] => {
  if (item.sn_principal === 'S') {
    const nonPrincipals = array.map((it) => ({ ...it, sn_principal: 'N' }))
    return [item, ...nonPrincipals]
  }
  return [...array, item]
}

const removeArrayItem = <T>(array: T[], index: number): T[] => {
  const newArray = [...array]
  newArray.splice(index, 1)
  return newArray
}

const DEFAULT_DADOS_CLIENTE_SIMULACAO: DefaultDadosCliente = {
  isEditado: false,
  formulariosPF: {} as FormsPF,
  formulariosPJ: {} as FormsPJ,
  devolucaoForm: {
    conta: {} as ContaBancariaData,
    pix: {} as Informacoespix,
    isConta: true,
    sigilo: false,
    typeAccountOption: 'C'
  } as DevolucaoForm,
  contatos: {
    telefones: [],
    emails: [],
    enderecos: []
  } as FormsContatoData
}

const useClienteRegistroStore = create(
  persist(
    combine(DEFAULT_DADOS_CLIENTE_SIMULACAO, (set) => ({
      setDevolucaoForm: (form: DevolucaoForm) => set(() => ({ devolucaoForm: form })),
      setFormulario: <
        T extends 'formulariosPF' | 'formulariosPJ',
        K extends keyof DefaultDadosCliente[T]
      >(
        formType: T,
        formKey: K,
        form: Partial<DefaultDadosCliente[T][K]>
      ) =>
        set((state) => ({
          [formType]: updateForm(state[formType], formKey, form)
        })),

      setFormularioPJSocio: (form: SocioType[]) =>
        set((state) => {
          const socioPrincipal = form.find((socio) => socio.sn_principal === 'S')
          const sociosData = socioPrincipal
            ? [socioPrincipal, ...form.filter((socio) => socio.sn_principal === 'N')]
            : form
          return {
            formulariosPJ: {
              ...state.formulariosPJ,
              formularioSocios: sociosData
            }
          }
        }),

      setFormEditado: (isEditado: boolean) => set(() => ({ isEditado })),
      setSocio: (socio: SocioType) =>
        set((state) => ({ formulariosPJ: { ...state.formulariosPJ, formularioSocios: [socio] } })),
      setContatoItem: (
        arrayName: 'enderecos' | 'telefones' | 'emails',
        item: Endereco | Telefone | Email
      ) =>
        set((state) => ({
          contatos: {
            ...state.contatos,
            [arrayName]: [...state.contatos[arrayName], item]
          }
        })),

      setFormularioPJSocioItem: (socio: SocioType) =>
        set((state) => ({
          formulariosPJ: {
            ...state.formulariosPJ,
            formularioSocios: addOrUpdatePrincipal(state.formulariosPJ.formularioSocios, socio)
          }
        })),

      removeItem: (arrayName: 'enderecos' | 'telefones' | 'emails' | 'socios', index: number) => {
        set((state) => {
          if (arrayName === 'socios') {
            const updatedSocios = removeArrayItem(state.formulariosPJ.formularioSocios, index)
            return {
              formulariosPJ: {
                ...state.formulariosPJ,
                formularioSocios: updatedSocios
              }
            }
          } else {
            const updatedArray = removeArrayItem(
              state.contatos[arrayName] as (Endereco | Telefone | Email)[],
              index
            )
            return {
              contatos: {
                ...state.contatos,
                [arrayName]: updatedArray
              }
            }
          }
        })
      },
      updateItemPrincipal: (arrayName: keyof PrincipalKeyMap, index: number) => {
        set((state) => {
          if (arrayName === 'socios') {
            const newArray = [...state.formulariosPJ.formularioSocios] as SocioType[]
            newArray.forEach((item, idx) => {
              if (idx === index) {
                item.sn_principal = 'S'
                item.telefone = { ...item.telefone, telefone_principal: true, telefone_sms: true }
              } else {
                item.sn_principal = 'N'
                item.telefone = { ...item.telefone, telefone_principal: false, telefone_sms: false }
              }
            })
            if (index !== 0) {
              const itemPrincipal = newArray.splice(index, 1)[0]
              newArray.unshift(itemPrincipal)
            }
            return {
              ...state,
              formulariosPJ: { ...state.formulariosPJ, formularioSocios: newArray as SocioType[] }
            }
          } else {
            const newArray = [...state.contatos[arrayName]] as (Endereco | Telefone | Email)[]
            if (arrayName === 'enderecos') {
              const updatedArray = newArray as Endereco[]
              updatedArray.forEach((item, idx) => {
                item.endereco_principal = idx === index
              })
            } else if (arrayName === 'telefones') {
              const updatedArray = newArray as Telefone[]
              updatedArray.forEach((item, idx) => {
                item.telefone_principal = idx === index
              })
            } else if (arrayName === 'emails') {
              const updatedArray = newArray as Email[]
              updatedArray.forEach((item, idx) => {
                item.e_mail_principal = idx === index
              })
            }
            if (index !== 0) {
              const itemPrincipal = newArray.splice(index, 1)[0]
              newArray.unshift(itemPrincipal)
            }

            return {
              ...state,
              contatos: {
                ...state.contatos,
                [arrayName]: newArray as (Endereco | Telefone | Email)[]
              }
            }
          }
        })
      },
      addNewPartner: (socio: SocioType) => {
        set((state) => {
          const existingPartnerIndex = state.formulariosPJ.formularioSocios.findIndex(
            (s) => s.cd_inscricao_nacional_socio === socio.cd_inscricao_nacional_socio
          )

          let updatedSocios = [...state.formulariosPJ.formularioSocios]

          if (existingPartnerIndex !== -1) {
            updatedSocios[existingPartnerIndex] = { ...socio }
          } else {
            updatedSocios.push(socio)
          }

          if (socio.sn_principal === 'S') {
            updatedSocios = updatedSocios.map((s) => {
              const updatedSocio = { ...s }

              updatedSocio.sn_principal =
                s.cd_inscricao_nacional_socio === socio.cd_inscricao_nacional_socio ? 'S' : 'N'

              updatedSocio.telefone = {
                ...s.telefone,
                telefone_principal:
                  s.cd_inscricao_nacional_socio === socio.cd_inscricao_nacional_socio,
                telefone_sms: s.cd_inscricao_nacional_socio === socio.cd_inscricao_nacional_socio
              }

              return updatedSocio
            })

            updatedSocios = updatedSocios.sort((a) => (a.sn_principal === 'S' ? -1 : 1))
          }

          return {
            ...state,
            formulariosPJ: { ...state.formulariosPJ, formularioSocios: updatedSocios }
          }
        })
      },

      resetRegister: () => set(DEFAULT_DADOS_CLIENTE_SIMULACAO)
    })),
    {
      name: 'dados-cliente-registro',
      storage: createJSONStorage(() => localStorage)
    }
  )
)

if (process.env.NODE_ENV === 'development') {
  mountStoreDevtool('User Register store', useClienteRegistroStore)
}

export default useClienteRegistroStore
